<script setup>
import { ref, computed } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FormFooter from './FormFooter.vue';

const emit = defineEmits(['sendMessage', 'cancel']);

const title = ref('');
const description = ref('');
const footerText = ref('');
const buttonText = ref('');

const newRow = () => ({ title: '', description: '' });
const newSection = () => ({ title: '', rows: [newRow()] });
const sections = ref([newSection()]);

const addSection = () => sections.value.push(newSection());
const removeSection = index => {
  if (sections.value.length <= 1) return;
  sections.value.splice(index, 1);
};
const addRow = section => section.rows.push(newRow());
const removeRow = (section, index) => {
  if (section.rows.length <= 1) return;
  section.rows.splice(index, 1);
};

const isValid = computed(
  () =>
    title.value.trim() &&
    description.value.trim() &&
    footerText.value.trim() &&
    sections.value.every(section => section.rows.some(row => row.title.trim()))
);

const serializeSections = () =>
  sections.value.map(section => ({
    title: section.title.trim() || undefined,
    rows: section.rows
      .filter(row => row.title.trim())
      .map(row => ({
        title: row.title.trim(),
        description: row.description.trim() || undefined,
      })),
  }));

const submit = () => {
  if (!isValid.value) return;

  emit('sendMessage', {
    message: '',
    contentType: 'list',
    contentAttributes: {
      data: {
        title: title.value.trim(),
        description: description.value.trim(),
        footerText: footerText.value.trim(),
        buttonText: buttonText.value.trim(),
        sections: serializeSections(),
      },
    },
  });
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <Input
      v-model="title"
      :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.LIST.TITLE')"
    />
    <TextArea
      v-model="description"
      :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.LIST.DESCRIPTION')"
    />
    <Input
      v-model="footerText"
      :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.LIST.FOOTER_TEXT')"
    />
    <Input
      v-model="buttonText"
      :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.LIST.BUTTON_TEXT')"
    />
    <div class="flex flex-col gap-3">
      <label class="text-sm font-medium text-n-slate-11">
        {{ $t('CONVERSATION.EVOLUTION_INTERACTIVE.LIST.SECTIONS') }}
      </label>
      <div
        v-for="(section, sectionIndex) in sections"
        :key="sectionIndex"
        class="flex flex-col gap-2 p-3 rounded-lg outline outline-1 outline-n-weak"
      >
        <div class="flex items-center gap-2">
          <Input
            v-model="section.title"
            class="grow"
            :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.LIST.SECTION_TITLE')"
          />
          <NextButton
            v-if="sections.length > 1"
            icon="i-lucide-x"
            ghost
            slate
            sm
            type="button"
            @click="removeSection(sectionIndex)"
          />
        </div>
        <div
          v-for="(row, rowIndex) in section.rows"
          :key="rowIndex"
          class="flex items-center gap-2 ltr:pl-3 rtl:pr-3"
        >
          <Input
            v-model="row.title"
            class="grow"
            :placeholder="
              $t('CONVERSATION.EVOLUTION_INTERACTIVE.LIST.ROW_TITLE')
            "
          />
          <Input
            v-model="row.description"
            class="grow"
            :placeholder="
              $t('CONVERSATION.EVOLUTION_INTERACTIVE.LIST.ROW_DESCRIPTION')
            "
          />
          <NextButton
            v-if="section.rows.length > 1"
            icon="i-lucide-x"
            ghost
            slate
            sm
            type="button"
            @click="removeRow(section, rowIndex)"
          />
        </div>
        <NextButton
          icon="i-lucide-plus"
          faded
          slate
          sm
          type="button"
          class="self-start"
          :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.LIST.ADD_ROW')"
          @click="addRow(section)"
        />
      </div>
      <NextButton
        icon="i-lucide-plus"
        faded
        slate
        sm
        type="button"
        class="self-start"
        :label="$t('CONVERSATION.EVOLUTION_INTERACTIVE.LIST.ADD_SECTION')"
        @click="addSection"
      />
    </div>
    <FormFooter
      :disabled="!isValid"
      @cancel="$emit('cancel')"
      @submit="submit"
    />
  </div>
</template>
